/**
 * Identitas penyelenggara layanan (nama badan usaha, kontak, status PSE).
 *
 * Sumber tunggalnya adalah runtimeConfig.public di nuxt.config.ts, yang
 * DEFAULT-nya kosong dan diisi lewat env saat deploy. Composable ini tidak
 * pernah mengarang nilai pengganti: bila sebuah data belum diisi, penanda
 * `ada*` bernilai false dan elemen yang menampilkannya wajib disembunyikan
 * dengan `v-if`. Dengan begitu situs tetap rapi (bukan menampilkan "undefined"
 * atau nomor palsu) sebelum pemiliknya melengkapi data.
 */

/** Konteks halaman untuk pesan awal WhatsApp, supaya kami tahu asal pertanyaan. */
export interface OpsiWa {
  /** Kalimat pembuka yang sudah terisi di kolom chat WhatsApp. */
  pesan?: string
  /** Nama halaman/produk asal, mis. "BerkasKilat" atau "Pembayaran". */
  konteks?: string
}

function bersih(v: unknown): string {
  // destr mengubah env yang seluruhnya angka (mis. NUXT_PUBLIC_WA_NUMBER=628123…)
  // menjadi number. Tanpa penanganan ini nomor WhatsApp yang sudah diisi akan
  // dianggap kosong dan semua tombol kontak ikut hilang.
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return typeof v === 'string' ? v.trim() : ''
}

/** Sisakan hanya angka; wa.me menolak spasi, tanda plus, dan tanda hubung. */
function angkaSaja(v: string): string {
  return v.replace(/\D/g, '')
}

/**
 * Ubah nomor Indonesia ke bentuk yang diterima wa.me: kode negara di depan,
 * tanpa angka nol awal.
 *
 * KENAPA PERLU. Orang Indonesia menuliskan nomornya sebagai `0851…` — itu
 * bentuk yang tertera di ponsel dan yang akan diketik pemilik ke env. Tapi
 * `wa.me/0851…` TIDAK VALID: WhatsApp menuntut kode negara. Tanpa normalisasi
 * ini, tombol bantuan tetap tampil, terlihat benar, dan gagal saat diklik —
 * kegagalan paling buruk bentuknya, karena baru ketahuan dari pembeli yang
 * sudah telanjur bingung.
 *
 * Dibuat menerima kedua bentuk supaya tidak ada cara "salah" mengisi env.
 */
export function normalNomorWa(mentah: string): string {
  const n = angkaSaja(mentah)
  if (n === '') return ''
  if (n.startsWith('62')) return n
  if (n.startsWith('0')) return '62' + n.slice(1)
  // Sudah tanpa nol dan tanpa kode negara (mis. "851…") — anggap Indonesia.
  return '62' + n
}

/**
 * Nomor dalam bentuk yang enak dibaca: +62 851-2153-3395.
 *
 * Pengelompokan lama `(\d{1,3})(\d{3,4})(\d{3,4})(\d+)` serakah di depan: ia
 * mengambil "628" sebagai kode negara lalu menyisakan ekor dua angka —
 * `+628 5121-5333-95`. Nomor Indonesia selalu 62 + prefiks operator 3 angka,
 * sisanya dikelompokkan empat-empat.
 */
export function formatNomorWa(nomor: string): string {
  if (nomor === '') return ''
  if (!nomor.startsWith('62')) return '+' + nomor

  const sisa = nomor.slice(2)
  if (sisa.length <= 3) return '+62 ' + sisa

  const bagian = [sisa.slice(0, 3)]
  for (let i = 3; i < sisa.length; i += 4) bagian.push(sisa.slice(i, i + 4))
  return '+62 ' + bagian.join('-')
}

export function useIdentitas() {
  const config = useRuntimeConfig()
  const p = config.public

  const namaBadanUsaha = bersih(p.namaBadanUsaha)
  const alamat = bersih(p.alamat)
  const waNumber = normalNomorWa(bersih(p.waNumber))
  const email = bersih(p.email)
  const tahunBerdiri = bersih(p.tahunBerdiri)
  // destr sudah mengubah env "true"/"false" menjadi boolean; paksa ke boolean
  // supaya nilai aneh (mis. string kosong) tidak dianggap terdaftar.
  const pseTerdaftar = p.pseTerdaftar === true

  /**
   * Tautan klik-untuk-chat WhatsApp. Mengembalikan string kosong bila nomor
   * belum diisi — pemanggil WAJIB memeriksa `adaWa` lebih dulu, bukan
   * menampilkan tautan mati.
   */
  function tautanWa(o: OpsiWa = {}): string {
    if (!waNumber) return ''
    const bagian = [o.pesan || 'Halo, saya butuh bantuan soal layanan tools.coreasia.id.']
    if (o.konteks) bagian.push(`(Halaman: ${o.konteks})`)
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(bagian.join(' '))}`
  }

  /** Tautan mailto dengan subjek yang sudah terisi. */
  function tautanEmail(subjek = 'Bantuan tools.coreasia.id'): string {
    if (!email) return ''
    return `mailto:${email}?subject=${encodeURIComponent(subjek)}`
  }

  /**
   * Nomor WhatsApp dalam bentuk yang enak dibaca (+62 851-2153-3395).
   * Hanya untuk tampilan; tautannya tetap memakai angka polos.
   */
  const waTampil = formatNomorWa(waNumber)

  /**
   * Daftar kanal kontak yang benar-benar tersedia. Dipakai halaman legal
   * supaya kalimat "hubungi kami lewat WhatsApp atau email" tidak muncul saat
   * satu pun kanal belum ada.
   */
  const adaWa = waNumber !== ''
  const adaEmail = email !== ''
  const adaKontak = adaWa || adaEmail

  return {
    namaBadanUsaha,
    alamat,
    waNumber,
    waTampil,
    email,
    pseTerdaftar,
    tahunBerdiri,
    adaNama: namaBadanUsaha !== '',
    adaAlamat: alamat !== '',
    adaWa,
    adaEmail,
    adaKontak,
    adaTahun: tahunBerdiri !== '',
    tautanWa,
    tautanEmail
  }
}
